import React, { FC, useEffect, useState } from 'react';
import { Collapse } from '@mui/material';
import { NavHashLink } from 'react-router-hash-link';
import { useLocation } from 'react-router';

import { useConfig } from 'Modules/core/config';
import { Button2, IconButton, Menu24, Stack, useDesktop, Box, Typography, useTablet, useMediaSwitch } from 'Modules/ui3';

import { headerRoutes, routes } from '../../routes/routes';
import { NavTab } from '../NavTab';
import { CfaHubIcon } from '../CfaHubIcon';
import { HeaderComponentProps, HeaderDesktopProps, HeaderMobileProps, MobileButtonProps } from './types';
import { useHeaderContext } from './HeaderContextProvider';


export * from './HeaderContextProvider';

const MobileButton: FC<MobileButtonProps> = ({ dark }) => {
    const [isOpen, setIsOpen] = useState(false);
    const clickHanlde = () => setIsOpen(prev => !prev);
    const { pathname, hash } = useLocation();
    useEffect(
        () => setIsOpen(false),
        [pathname, hash],
    );
    return (
        <Box position='relative'>
            <IconButton
                onClick={clickHanlde}
                size='large'
                variant={dark ? 'primary' : 'secondary'}
                sx={{
                    borderRadius: 4,
                    bgcolor: dark ? 'rgba(255, 255, 255, 0.08)' : '#F5F5F5',
                    transition: 'background-color 0.3s',
                }}
            >
                <Menu24
                    sx={{
                        fontSize: '20xp',
                        color: dark ? '#fff' : '#242424',
                        transition: 'background-color 0.3s',
                    }}
                />
            </IconButton>
            <Box position='absolute' right='0' top='48px'>
                <Collapse in={isOpen}>
                    <Stack bgcolor='#fff' borderRadius={8} p={5} spacing={5}>
                        {headerRoutes.map(key => routes.home.anchors[key])
                            .map(({ route, name }) => (
                                <Typography
                                    key={route}
                                    // @ts-ignore
                                    component={NavHashLink}
                                    to={`/${route}`}
                                    exact
                                    smooth
                                    fontStyle='Hauora'
                                    fontSize='16px'
                                    lineHeight='24px'
                                    fontWeight={600}
                                    color='#242424'
                                >
                                    {name}
                                </Typography>
                            ))}
                    </Stack>
                </Collapse>
            </Box>
        </Box>
    );
};

const HeaderComponent: FC<HeaderComponentProps> = ({
    isDesktop, isMobile, isTablet, isWhite, position = 'absolute', ...props
}) => {
    const lkflUrl = useConfig('lkflUrl');
    const showAuthorization = useConfig('showAuthorization');
    return (
        <Stack
            width='100%'
            alignItems='center'
            px={useMediaSwitch(8, 4, 2)}
            pt={isDesktop && !isWhite ? 8 : 2}
            {...isDesktop && isWhite && {
                pb: 2,
            }}
            position={position}
            top='0'
            left='50%'
            zIndex={100}
            sx={{
                transform: 'translate(-50%, 0)',
                transition: 'top 0.3s',
            }}
            {...props}
        >
            <Stack
                component='nav'
                direction='row'
                boxSizing='border-box'
                spacing={isDesktop ? 4 : 2}
                justifyContent='space-between'
                p={2}
                bgcolor={isWhite ? 'grayscale.shade0' : 'transparent'}
                borderRadius={6}
                width='100%'
                maxWidth={isWhite ? '1200px' : '1840px'}
                sx={{
                    ...isWhite && {
                        backdropFilter: 'blur(12px)',
                        boxShadow: '0 0 80px 0 rgba(0, 0, 0, 0.08)',
                    },
                    transition: 'background-color 0.3s',
                }}
            >
                <Box flex={isMobile || !isWhite ? 1 : 0}>
                    <CfaHubIcon
                        dark={!isWhite}
                        size={isDesktop ? 12 : 10}
                    />
                </Box>
                {isDesktop && (
                    <Stack
                        direction='row'
                        {...!isWhite ? {
                            spacing: 2,
                        } : {
                            bgcolor: '#F4F4F4',
                            borderRadius: '100px',
                        }}
                    >
                        {headerRoutes.map(key => routes.home.anchors[key])
                            .map(({ route, name }) => (
                                <NavTab
                                    key={route}
                                    route={route}
                                    title={name}
                                    isWhite={isWhite}
                                />
                            ))}
                    </Stack>
                )}
                {showAuthorization && (
                    <Button2
                        LinkComponent='a'
                        href={lkflUrl}
                        variant={isWhite ? 'primary' : 'stroke'}
                        variantType='main'
                        size={isDesktop ? 'medium' : 'small'}
                        sx={{
                            borderRadius: isDesktop ? 5 : 4,
                            fontFamily: 'Hauora',
                            color: isWhite ? '#FFF' : '#242424',
                            fontWeight: 700,
                        }}
                    >
                        Войти
                    </Button2>
                )}
                {!isDesktop && <MobileButton dark={!isWhite} />}
            </Stack>
        </Stack>
    );
};

const HeaderMobile: FC<HeaderMobileProps> = ({ isWhite }) => {
    const isTablet = useTablet();
    return (
        <HeaderComponent
            isMobile
            isTablet={isTablet}
            isWhite={isWhite}
            position='fixed'
        />
    );
};

const HeaderDesktop: FC<HeaderDesktopProps> = ({ isSticky }) => (
    <>
        <HeaderComponent isDesktop position='absolute' />
        <HeaderComponent
            isDesktop
            isWhite
            position='fixed'
            {...!isSticky && {
                top: '-100%',
            }}
        />
    </>
);

export const Header: FC = () => {
    const isDesktop = useDesktop();
    const { isTriggerHide } = useHeaderContext();
    return isDesktop
        ? <HeaderDesktop isSticky={isTriggerHide} />
        : <HeaderMobile isWhite={isTriggerHide} />;
};
